import { useState } from "react";
import { choosable, lastOfCourse, marksSoldOut, menuView, setKey, setSoldOut, withDrinksOf } from "@speisekarte/core";
import { api, dishesById, messageOf, store, textsById, useStore } from "../store.ts";
import { leadNow } from "./Web.tsx";
import { Toast, useToast } from "./Toast.tsx";

/**
 * „Ausverkauft“ (S39): the dishes of the menu on the net, one tap marks one sold out and the guests
 * see it at once; another tap and it is there again. Until 4 o'clock next morning, then by itself.
 * Paper stays as it is. A set menu (S32) has one row as a whole; its dishes alone only where the
 * guests choose between them („oder“), as `setSoldOut` marks them on the net; one of them stays.
 */
export function SoldOut() {
  const s = useStore();
  const data = s.data!;
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const may = marksSoldOut(s.role);
  const published = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
  const back = (
    <p>
      <a className="back" href="#/netz">
        ← Im Netz
      </a>
    </p>
  );
  if (!published) {
    return (
      <div>
        {back}
        <section className="card">
          <h2>Ausverkauft</h2>
          <p>Gäste sehen zurzeit keine QR-Code-Speisekarte.</p>
        </section>
      </div>
    );
  }

  // The dishes as the guests see them, with the drinks of another menu when so (S22).
  const from = data.settings.drinksMenu;
  const menu = withDrinksOf(published, from && from !== published.id ? (data.menus.find((m) => m.id === from) ?? null) : null);
  const view = menuView(menu, dishesById(data), data.allergens, textsById(data), data.tagIcons);
  // The recommendations above it (S37, L2) first, as the guests see them.
  const lead = leadNow(data, published.id);
  const now = Date.now();
  const marks = data.soldOut.filter((x) => x.until > now);
  const sold = new Set(marks.flatMap((x) => (x.dish !== undefined ? [x.dish] : [])));
  const soldSets = new Set(marks.flatMap((x) => (x.set !== undefined ? [x.set] : [])));
  const q = query.trim().toLowerCase();
  const found = (name: string, number: number | null) => !q || name.toLowerCase().includes(q) || String(number ?? "") === q;
  type Item = { dish: string; number: number | null; name: string; text?: true; or?: true; drink?: true };
  type Row = { dish: string; number: number | null; name: string; last?: true } | { set: string; name: string; own: boolean; because: Item[] };
  // A set menu: one row as a whole, out when marked or when a course has nothing left (`because`);
  // then the dishes to choose from.
  const rowsOf = (menuId: string, sec: { id?: string; title: string; kind: string }, items: Item[]): Row[] => {
    const dishes = items.filter((i) => !i.text);
    if (sec.kind !== "set" || !sec.id) return dishes.filter((i) => found(i.name, i.number));
    const key = setKey(menuId, sec.id);
    const name = sec.title || "Menü";
    const isOut = (i: Item) => !i.text && sold.has(i.dish);
    const because = setSoldOut(items, isOut).empty.map((k) => items[k]!);
    const last = new Set(lastOfCourse(items, isOut));
    const choice = choosable(items)
      .map((k) => ({ ...items[k]!, ...(last.has(k) ? { last: true as const } : {}) }))
      .filter((i) => found(i.name, i.number));
    return [...(found(name, null) || choice.length ? [{ set: key, name, own: soldSets.has(key), because }] : []), ...choice];
  };
  const all = [
    ...(lead ? lead.sections.map((sec, k) => ({ title: k === 0 ? sec.title || lead.title || "Empfehlungen" : sec.title, rows: rowsOf(lead.menu, sec, sec.items.flatMap((i) => (i.dish ? [{ ...i, dish: i.dish }] : i.text ? [{ ...i, dish: "" }] : []))) })) : []),
    ...view.sections.map((sec) => ({ title: sec.title, rows: rowsOf(published.id, sec, sec.items) })),
  ];
  const sections = all.filter((sec) => sec.rows.length > 0);
  const setsOut = soldSets.size;
  const count = [setsOut ? (setsOut === 1 ? "1 Menü" : `${setsOut} Menüs`) : "", sold.size ? (sold.size === 1 ? "1 Speise" : `${sold.size} Speisen`) : ""].filter(Boolean).join(" und ");

  const toggle = async (body: { dish: string; soldOut: boolean } | { set: string; soldOut: boolean; dishes?: string[] }) => {
    setBusy("dish" in body ? body.dish : body.set);
    try {
      await store.change(() => api("/api/sold-out", { body }));
    } catch (err) {
      toast.show(messageOf(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      {back}
      <section className="card">
        <h2>Ausverkauft</h2>
        <p className="hint">
          Ein Tipp, und die Gäste sehen es sofort auf der QR-Code-Speisekarte „{published.title}“. Es gilt bis morgen früh um 4 Uhr, dann ist die Speise von selbst wieder da. Ein Menü ist als Ganzes ausverkauft, eine Speise darin einzeln nur, wo die Gäste wählen. Gedruckte Speisekarten bleiben, wie sie sind.
        </p>
        <p className="muted small" data-testid="sold-out-count">
          {count ? `${count} ausverkauft` : "Nichts ausverkauft"}
        </p>
        <input type="search" placeholder="Suchen: Name oder Nummer" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="sold-out-search" />
      </section>
      {sections.map((sec, k) => (
        <section key={k} className="card">
          {sec.title && <h2>{sec.title}</h2>}
          <ul className="list" style={{ padding: "0 0.4rem" }}>
            {sec.rows.map((r, n) => {
              if ("set" in r) {
                const on = r.own || r.because.length > 0;
                // Back again with the dishes that kept it out (Martin, 31.10.2026).
                const flip = () => void toggle(on ? { set: r.set, soldOut: false, ...(r.because.length ? { dishes: r.because.map((i) => i.dish) } : {}) } : { set: r.set, soldOut: true });
                return (
                  <li key={`${r.set}-${n}`}>
                    <button className={`dish-row sold-row ${on ? "sold" : ""}`} aria-pressed={on} disabled={!may || busy !== null} onClick={flip} data-testid={`sold-set-${r.name}`}>
                      <span className="no"></span>
                      <span className="name">
                        <b>Das ganze Menü</b>
                        {r.because.length > 0 && <span className="facts">{r.because.map((i) => `„${i.name}“`).join(", ")} ausverkauft</span>}
                      </span>
                      <span className={`sold-state ${on ? "on" : ""}`}>{on ? "ausverkauft" : "da"}</span>
                    </button>
                  </li>
                );
              }
              const on = sold.has(r.dish);
              // The last of a course to choose from stays (Martin, 31.10.2026): for none, the whole menu.
              const stays = Boolean(r.last) && !on;
              return (
                <li key={`${r.dish}-${n}`}>
                  <button className={`dish-row sold-row ${on ? "sold" : ""}`} aria-pressed={on} disabled={!may || busy !== null || stays} onClick={() => void toggle({ dish: r.dish, soldOut: !on })} data-testid={`sold-${r.number ?? r.dish}`}>
                    <span className="no">{r.number}</span>
                    <span className="name">
                      <b>{r.name}</b>
                      {stays && <span className="facts">Die letzte zur Wahl; sonst das ganze Menü</span>}
                    </span>
                    <span className={`sold-state ${on ? "on" : ""}`}>{on ? "ausverkauft" : "da"}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {sections.length === 0 && <p className="muted">Keine Speise gefunden.</p>}
      <Toast toast={toast} />
    </div>
  );
}
